<template>
  <Teleport to="body">
    <Transition name="modal-fade">
      <div v-if="visible" class="modal-overlay" @click.self="close">
      <div class="custom-modal github-download-modal">
        <div class="modal-header">
          <h3>
            <i class="fa-brands fa-github" style="margin-right: 8px;"></i>
            GitHub 代理获取
          </h3>
          <button class="close-btn" @click="close">
            <i class="fa-solid fa-times"></i>
          </button>
        </div>

        <div class="modal-body">
          <!-- 阶段1: 加载中 -->
          <div v-if="stage === 'loading'" class="stage-loading">
            <div class="loading-spinner-large">
              <i class="fa-solid fa-spinner fa-spin"></i>
            </div>
            <p class="loading-text">正在获取最新版本信息...</p>
          </div>

          <!-- 阶段1 暂无版本（正常情况：仓库未发 release/未传附件，不是错误，友好展示） -->
          <div v-else-if="stage === 'empty'" class="stage-empty">
            <div class="empty-icon">
              <i class="fa-solid fa-box-open"></i>
            </div>
            <p class="empty-title">{{ emptyTitle }}</p>
            <p class="empty-text">{{ emptyMessage }}</p>
            <div class="empty-actions">
              <a v-if="githubUrl" class="empty-link-btn" :href="githubUrl" target="_blank" rel="noopener">
                <i class="fa-brands fa-github"></i> 去仓库看看
              </a>
              <button class="retry-btn" @click="fetchRelease">
                <i class="fa-solid fa-rotate-right"></i> 刷新试试
              </button>
            </div>
          </div>

          <!-- 阶段1 错误 -->
          <div v-else-if="stage === 'error'" class="stage-error">
            <div class="error-icon">
              <i class="fa-solid fa-circle-exclamation"></i>
            </div>
            <p class="error-text">{{ errorMessage }}</p>
            <button class="retry-btn" @click="fetchRelease">
              <i class="fa-solid fa-rotate-right"></i> 重试
            </button>
          </div>

          <!-- 阶段2: 文件选择 -->
          <div v-else-if="stage === 'select'" class="stage-select">
            <div class="release-info">
              <span class="release-tag">{{ releaseVersion }}</span>
              <span class="release-date" v-if="releaseDate">{{ releaseDate }}</span>
            </div>

            <div class="tip-text">
              <i class="fa-solid fa-circle-info"></i>
              选择要下载的文件，将通过代理加速下载
            </div>

            <div class="file-list">
              <button
                v-for="asset in displayAssets"
                :key="asset.name"
                class="file-item"
                @click="selectFile(asset)"
              >
                <div class="file-icon">
                  <i :class="getFileIcon(asset.name)"></i>
                </div>
                <div class="file-info">
                  <span class="file-name">{{ asset.name }}</span>
                  <span class="file-meta">
                    {{ formatSize(asset.size) }}
                    <span v-if="asset.downloadCount" class="file-downloads">
                      · {{ asset.downloadCount }} 次下载
                    </span>
                  </span>
                </div>
                <div class="file-right">
                  <span class="arch-tag" :class="getArchTagClass(asset.name)">{{ getArchTagLabel(asset.name) }}</span>
                  <i class="fa-solid fa-download file-dl-icon"></i>
                </div>
              </button>
            </div>
          </div>

          <!-- 阶段2.5: 模式选择 -->
          <div v-else-if="stage === 'mode-select'" class="stage-mode-select">
            <div class="mode-select-header">
              <div class="file-badge">
                <i :class="getFileIcon(selectedFile?.name)"></i>
                {{ selectedFile?.name }}
              </div>
              <h4 class="mode-title">选择获取策略</h4>
            </div>

            <div class="mode-cards">
              <div class="mode-card auto" @click="startMode('auto')">
                <div class="card-icon">
                  <i class="fa-solid fa-gauge-high"></i>
                </div>
                <div class="card-content">
                  <div class="card-title">自动加速</div>
                  <div class="card-desc">自动测试所有节点，智能匹配最快路径开始下载</div>
                </div>
                <div class="card-arrow">
                  <i class="fa-solid fa-chevron-right"></i>
                </div>
              </div>

              <div class="mode-card manual" @click="startMode('manual')">
                <div class="card-icon">
                  <i class="fa-solid fa-list-ul"></i>
                </div>
                <div class="card-content">
                  <div class="card-title">手动选择</div>
                  <div class="card-desc">直接展示所有下载镜像，由您手动点击选择</div>
                </div>
                <div class="card-arrow">
                  <i class="fa-solid fa-chevron-right"></i>
                </div>
              </div>
            </div>

            <button class="back-btn" @click="stage = 'select'">
              <i class="fa-solid fa-arrow-left"></i> 返回重新选文件
            </button>
          </div>

          <!-- 阶段3: 节点展示/测速 -->
          <div v-else-if="stage === 'testing'" class="stage-testing">
            <div class="testing-header-container">
              <div class="testing-header">
                <i v-if="selectionMode === 'auto'" class="fa-solid fa-gauge-high"></i>
                <i v-else class="fa-solid fa-list-ul"></i>
                {{ selectionMode === 'auto' ? '正在测试代理节点速度...' : '点击选择下载镜像' }}
              </div>

              <!-- 模式切换(顶部小切换器) -->
              <div class="mode-selector">
                <button 
                  class="mode-tab" 
                  :class="{ active: selectionMode === 'auto' }" 
                  @click="startMode('auto')"
                >
                  自动
                </button>
                <button 
                  class="mode-tab" 
                  :class="{ active: selectionMode === 'manual' }" 
                  @click="startMode('manual')"
                >
                  手动
                </button>
              </div>
            </div>

            <div class="testing-file">
              <i class="fa-solid fa-file"></i> {{ selectedFile?.name }}
            </div>

            <!-- 自动下载状态条 -->
            <div 
              v-if="selectionMode === 'auto' && (countdown > 0 || usedProxy)" 
              class="auto-ready-info"
            >
              <div class="auto-pulse-icon">
                <i v-if="countdown > 0" class="fa-solid fa-bolt-lightning fa-fade"></i>
                <i v-else class="fa-solid fa-spinner fa-spin"></i>
              </div>
              <div class="auto-text">
                <template v-if="countdown > 0">
                  发现加速节点，将在 <strong>{{ countdown }}</strong> 秒后自动开始下载...
                </template>
                <template v-else-if="usedProxy && stage !== 'downloading'">
                  正在为您跳转下载...
                </template>
                <template v-else>
                  正在对比节点性能...
                </template>
              </div>
              <button class="stop-auto-btn" @click="setSelectionMode('manual')">
                我手动选
              </button>
            </div>

            <div class="proxy-test-list" :class="{ 'manual-mode': selectionMode === 'manual' }">
              <div
                v-for="(proxy, index) in proxyResults"
                :key="index"
                class="proxy-test-item"
                :class="{
                  'is-testing': proxy.status === 'testing',
                  'is-success': proxy.status === 'done' && proxy.speed > 0,
                  'is-failed': proxy.status === 'done' && proxy.speed <= 0,
                  'is-fastest': proxy.isFastest,
                  'clickable': selectionMode === 'manual' && proxy.status === 'done'
                }"
                @click="selectionMode === 'manual' && proxy.status === 'done' && selectProxy(proxy)"
              >
                <div class="proxy-name">
                  <i v-if="proxy.status === 'testing'" class="fa-solid fa-spinner fa-spin proxy-spinner"></i>
                  <i v-else-if="proxy.status === 'done' && proxy.speed > 0" class="fa-solid fa-check-circle proxy-ok"></i>
                  <i v-else-if="proxy.status === 'done'" :class="selectionMode === 'manual' ? 'fa-solid fa-circle-play proxy-wait' : 'fa-solid fa-times-circle proxy-fail'"></i>
                  <i v-else class="fa-solid fa-circle proxy-wait"></i>
                  <span>{{ proxy.name }}</span>
                  <span v-if="proxy.isFastest" class="fastest-badge">最快</span>
                  <span v-else-if="proxy.serverMeasured" class="server-badge"
                    title="服务端每日吞吐实测">实测</span>
                </div>
                <div class="proxy-speed-bar">
                  <div class="speed-bar-bg">
                    <div
                      class="speed-bar-fill"
                      :style="{ width: getSpeedBarWidth(proxy) }"
                    ></div>
                  </div>
                  <span class="speed-value" v-if="proxy.status === 'done' && proxy.speed > 0">
                    {{ (proxy.speed / 1024).toFixed(1) }} KB/s
                  </span>
                  <span class="speed-value speed-fail" v-else-if="proxy.status === 'done'">
                    {{ selectionMode === 'manual' ? '待选择' : '不可用' }}
                  </span>
                  <span class="speed-value speed-testing" v-else-if="proxy.status === 'testing'">
                    测速中...
                  </span>
                </div>
                <!-- 手动选择图标 -->
                <div v-if="selectionMode === 'manual' && proxy.status === 'done' && proxy.speed > 0" class="manual-select-hint">
                  使用此节点 <i class="fa-solid fa-chevron-right"></i>
                </div>
              </div>
            </div>

            <div v-if="noProxyAvailable" class="no-proxy-tip">
              <i class="fa-solid fa-triangle-exclamation"></i>
              所有代理节点均不可用，将使用原始链接
            </div>
          </div>

          <!-- 阶段4: 下载中 -->
          <div v-else-if="stage === 'downloading'" class="stage-downloading">
            <div class="download-success-icon">
              <i class="fa-solid fa-circle-check"></i>
            </div>
            <p class="download-text">正在开始下载...</p>
            <div class="download-file-name">{{ selectedFile?.name }}</div>
            <div class="download-proxy" v-if="usedProxy">
              <i class="fa-solid fa-bolt"></i> 通过 {{ usedProxy.name }} 加速
            </div>
            <a
              :href="finalDownloadUrl"
              class="manual-download-link"
              target="_blank"
              rel="noopener"
            >
              如果下载未开始，点击这里手动下载
            </a>
          </div>
        </div>

        <div class="modal-footer">
          <button class="cancel-btn" @click="close">
            {{ stage === 'downloading' ? '关闭' : '取消' }}
          </button>
        </div>
      </div>
    </div>
  </Transition>
  </Teleport>
</template>

<script>
import axios from 'axios'
import serverUrl from '@/assets/BaseServerUrl.js'
import { getProxyList, applyProxy } from '@/utils/githubProxy.js'

const ARCH_RULES = [
  { pattern: /\.deb$/i, os: 'debian', label: 'Debian', tagClass: 'tag-linux' },
  { pattern: /\.rpm$/i, os: 'redhat', label: 'RPM', tagClass: 'tag-linux' },
  { pattern: /\.AppImage$/i, os: 'linux', label: 'Linux', tagClass: 'tag-linux' },
  { pattern: /\.flatpak$/i, os: 'linux', label: 'Flatpak', tagClass: 'tag-linux' },
  { pattern: /\.snap$/i, os: 'linux', label: 'Snap', tagClass: 'tag-linux' },
  { pattern: /\.apk$/i, os: 'android', label: 'Android', tagClass: 'tag-android' },
  { pattern: /\.dmg$/i, os: 'macos', label: 'macOS', tagClass: 'tag-macos' },
  { pattern: /\.pkg$/i, os: 'macos', label: 'macOS', tagClass: 'tag-macos' },
  { pattern: /\.exe$/i, os: 'windows', label: 'Windows', tagClass: 'tag-windows' },
  { pattern: /\.msi$/i, os: 'windows', label: 'Windows', tagClass: 'tag-windows' },
  { pattern: /\.zip$/i, os: 'unknown', label: 'ZIP', tagClass: 'tag-generic' },
  { pattern: /\.tar\.gz$/i, os: 'unknown', label: 'Tar.gz', tagClass: 'tag-generic' },
  { pattern: /\.tar\.xz$/i, os: 'unknown', label: 'Tar.xz', tagClass: 'tag-generic' },
  { pattern: /\.7z$/i, os: 'unknown', label: '7z', tagClass: 'tag-generic' },
]

const CPU_RULES = [
  { pattern: /amd64|x86_64|x64(?![a-z])/i, label: 'x64', arch: 'x64' },
  { pattern: /arm64|aarch64/i, label: 'ARM64', arch: 'arm64' },
  { pattern: /armv[67]|armhf/i, label: 'ARM', arch: 'arm' },
  { pattern: /i386|i686|x86(?!_64)/i, label: 'x86', arch: 'x86' },
  { pattern: /riscv64/i, label: 'RISC-V', arch: 'riscv64' },
  { pattern: /loongarch64/i, label: 'LoongArch', arch: 'loongarch64' },
]

export default {
  name: 'GithubDownloadModal',
  props: {
    visible: { type: Boolean, default: false },
    githubUrl: { type: String, default: '' },
    appId: { type: [Number, String], default: null },
    initialFile: { type: Object, default: null }
  },
  emits: ['update:visible', 'downloaded'],
  data() {
    return {
      stage: 'loading',
      errorMessage: '',
      emptyTitle: '',
      emptyMessage: '',
      releaseVersion: '',
      releaseDate: '',
      assets: [],
      selectedFile: null,
      proxyResults: [],
      maxSpeed: 0,
      noProxyAvailable: false,
      usedProxy: null,
      finalDownloadUrl: '',
      selectionMode: 'auto',
      autoDownloadTimer: null,
      countdown: 0
    }
  },
  watch: {
    visible(val) {
      if (val) {
        this.resetState()
        if (this.initialFile && (this.initialFile.downloadUrl || this.initialFile.url)) {
          this.selectedFile = {
            name: this.initialFile.name,
            size: this.initialFile.size,
            downloadUrl: this.initialFile.downloadUrl || this.initialFile.url
          }
          this.releaseVersion = this.initialFile.version || ''
          this.stage = 'mode-select'
        } else {
          this.fetchRelease()
        }
      }
    }
  },
  computed: {
    displayAssets() {
      return this.assets || []
    }
  },
  methods: {
    close() {
      this.$emit('update:visible', false)
    },
    resetState() {
      this.stage = 'loading'
      this.errorMessage = ''
      this.releaseVersion = ''
      this.releaseDate = ''
      this.assets = []
      this.selectedFile = null
      this.proxyResults = []
      this.maxSpeed = 0
      this.noProxyAvailable = false
      this.usedProxy = null
      this.finalDownloadUrl = ''
      if (this.autoDownloadTimer) {
        clearInterval(this.autoDownloadTimer)
        this.autoDownloadTimer = null
      }
      this.countdown = 0
    },
    parseGithubUrl() {
      if (!this.githubUrl) return null
      // 从 GitHub URL 中提取 owner/repo
      const match = this.githubUrl.match(/github\.com\/([^/]+)\/([^/]+)/)
      if (!match) return null
      return { owner: match[1], repo: match[2].replace(/\.git$/, '') }
    },
    async fetchRelease() {
      this.stage = 'loading'
      const parsed = this.parseGithubUrl()
      if (!parsed) {
        this.stage = 'error'
        this.errorMessage = '无法解析 GitHub 仓库地址'
        return
      }
      try {
        const res = await axios.get(`${serverUrl}/api/github/release/${parsed.owner}/${parsed.repo}`)
        if (res.data.success) {
          this.releaseVersion = res.data.version
          this.releaseDate = res.data.publishedAt
            ? new Date(res.data.publishedAt).toLocaleDateString('zh-CN')
            : ''
          this.assets = res.data.assets || []
          if (this.assets.length === 0) {
            // 正常情况（作者未传附件），不是网站错误——委婉展示而非报错
            this.stage = 'empty'
            this.emptyTitle = '这个版本还没有附上安装包'
            this.emptyMessage = `作者发布 ${res.data.version || '最新版本'} 时还没有上传可下载的文件，可能过几天就有了`
          } else {
            this.stage = 'select'
          }
        } else if (res.data.noRelease) {
          this.stage = 'empty'
          this.emptyTitle = '还没有发布可下载的版本'
          this.emptyMessage = '这个仓库暂时没有发布任何版本，不妨先逛逛别的内容～'
        } else {
          this.stage = 'error'
          this.errorMessage = res.data.error || '获取版本信息失败'
        }
      } catch (err) {
        if (err.response?.status === 404) {
          // GitHub API 对没有 release 的仓库返回 404——这是正常情况，委婉展示
          this.stage = 'empty'
          this.emptyTitle = '还没有发布可下载的版本'
          this.emptyMessage = '这个仓库暂时没有发布任何版本，不妨先逛逛别的内容～'
          return
        }
        this.stage = 'error'
        // 技术细节不暴露给用户，委婉化
        this.errorMessage = '暂时获取不到版本信息，可能是网络开小差了，稍后再试试吧'
      }
    },
    async selectFile(asset) {
      this.selectedFile = asset
      this.stage = 'mode-select'
    },
    async startMode(mode) {
      this.selectionMode = mode
      this.stage = 'testing'
      // 如果是自动模式，需要测速；如果是手动模式，不需要测速
      await this.runSpeedTest(this.selectedFile.downloadUrl, mode === 'auto')
    },
    async runSpeedTest(originalUrl, shouldTest = true) {
      // 获取代理列表
      try {
        const proxies = await getProxyList()
        if (!proxies.length) {
          // 无代理，直接下载
          this.startDirectDownload(originalUrl)
          return
        }

        // 服务端每天已对全部插件节点做过吞吐精测（speedKBps），直接预填结果；
        // 只有手动节点和服务端无测速数据的节点需要现场测速，大幅减少用户等待
        this.proxyResults = proxies.map(p => ({
          name: p.name || p.url,
          url: p.url,
          type: p.type,
          status: p.speedKBps != null ? 'done' : (shouldTest ? 'waiting' : 'done'),
          speed: p.speedKBps != null ? p.speedKBps * 1024 : 0,
          serverMeasured: p.speedKBps != null,
          isFastest: false
        }))

        // 添加直连选项
        this.proxyResults.push({
          name: '直连 (无代理)',
          url: '',
          type: 'direct',
          status: shouldTest ? 'waiting' : 'done',
          speed: 0,
          isFastest: false
        })

        if (!shouldTest) {
          // 不测速，直接结束
          return
        }

        // 只测没有服务端数据的节点（手动节点 + 直连）；
        // 直连可用时它会在下面统一排序中胜出，自动优先直连
        const needTest = this.proxyResults.filter(p => !p.serverMeasured)
        if (needTest.length) {
          const testPromises = needTest.map(proxy =>
            this.testSingleProxy(proxy, this.proxyResults.indexOf(proxy), originalUrl)
          )
          await Promise.allSettled(testPromises)
        }

        // 找出最快的（服务端预填结果与实测结果一起参与排序）
        this.maxSpeed = 0
        let fastestIdx = -1
        this.proxyResults.forEach((p, i) => {
          if (p.speed > this.maxSpeed) {
            this.maxSpeed = p.speed
            fastestIdx = i
          }
        })

        if (fastestIdx >= 0) {
          this.proxyResults[fastestIdx].isFastest = true
          const fastest = this.proxyResults[fastestIdx]
          
          if (this.selectionMode === 'auto') {
            this.usedProxy = fastest
            const finalUrl = applyProxy(originalUrl, fastest)
            
            // 自动下载流程：开始 3 秒倒计时
            this.countdown = 3
            this.autoDownloadTimer = setInterval(() => {
              if (this.countdown > 0) {
                this.countdown--
              } else {
                clearInterval(this.autoDownloadTimer)
                this.autoDownloadTimer = null
                this.startDownload(finalUrl)
              }
            }, 1000)
          } else {
            // 手动模式下（虽然这里 shouldTest=true 不会发生，除非是从界面切换的）
            this.usedProxy = fastest
          }
        } else {
          this.noProxyAvailable = true
          if (this.selectionMode === 'auto') {
            this.countdown = 3
            this.autoDownloadTimer = setInterval(() => {
              if (this.countdown > 0) {
                this.countdown--
              } else {
                clearInterval(this.autoDownloadTimer)
                this.autoDownloadTimer = null
                this.startDirectDownload(originalUrl)
              }
            }, 1000)
          }
        }
      } catch (err) {
        console.error('测速异常:', err)
        this.startDirectDownload(originalUrl)
      }
    },
    setSelectionMode(mode) {
      if (this.selectionMode === mode) return
      this.selectionMode = mode
      
      // 如果切到手动模式，立刻取消自动下载定时器
      if (mode === 'manual' && this.autoDownloadTimer) {
        clearInterval(this.autoDownloadTimer)
        this.autoDownloadTimer = null
        this.countdown = 0
      }
    },
    async selectProxy(proxy) {
      // 如果还在自动等待中，取消它
      if (this.autoDownloadTimer) {
        clearInterval(this.autoDownloadTimer)
        this.autoDownloadTimer = null
        this.countdown = 0
      }

      const originalUrl = this.selectedFile.downloadUrl
      this.usedProxy = proxy
      const finalUrl = applyProxy(originalUrl, proxy)
      this.startDownload(finalUrl)
    },
    async testSingleProxy(proxy, index, originalUrl) {
      this.proxyResults[index].status = 'testing'
      try {
        let testUrl
        if (proxy.type === 'direct') {
          // 直连测试用原始 URL
          testUrl = originalUrl
        } else {
          testUrl = applyProxy(
            'https://raw.githubusercontent.com/torvalds/linux/master/MAINTAINERS',
            proxy
          )
        }

        const startTime = Date.now()
        const controller = new AbortController()
        const TOTAL_DEADLINE = 6000 // 整体硬超时：含连接+body读取，防慢滴流节点永久卡在"测速中"
        const deadlineId = setTimeout(() => controller.abort(), TOTAL_DEADLINE)
        const timeoutId = setTimeout(() => controller.abort(), 3000)

        try {
          const response = await fetch(testUrl, {
            signal: controller.signal,
            mode: 'cors',
            cache: 'no-store'
          })
          clearTimeout(timeoutId) // 响应头已到；后续由整体 deadline 兑底

          if (!response.ok) {
            this.proxyResults[index].speed = 0
            return
          }
          if (!response.body) {
            return
          }

          // 读取前 50KB 数据来测速；每次 read() 受整体 deadline 兑底，
          // deadline 内读不到 10KB 视为带宽过低，判死
          const reader = response.body.getReader()
          let totalBytes = 0
          const maxBytes = 50 * 1024
          const MIN_BYTES = 10 * 1024

          while (totalBytes < maxBytes) {
            const { done, value } = await reader.read()
            if (done) break
            totalBytes += value.length
          }
          reader.cancel().catch(() => {})

          const elapsed = (Date.now() - startTime) / 1000
          this.proxyResults[index].speed = totalBytes < MIN_BYTES ? 0 : (elapsed > 0 ? totalBytes / elapsed : 0)
        } finally {
          clearTimeout(timeoutId)
          clearTimeout(deadlineId)
        }
      } catch (e) {
        this.proxyResults[index].speed = 0
      } finally {
        this.proxyResults[index].status = 'done'
      }
    },
    startDirectDownload(url) {
      this.usedProxy = null
      this.startDownload(url)
    },
    startDownload(url) {
      this.finalDownloadUrl = url
      this.stage = 'downloading'
      // 发送下载统计
      if (this.appId) {
        axios.post(`${serverUrl}/api/software/increment-download`, {
          id: this.appId,
          type: '5'
        }).catch(() => {})
      }
      this.$emit('downloaded')
      // 触发浏览器下载
      window.open(url, '_blank')
    },
    formatSize(bytes) {
      if (!bytes) return '未知大小'
      if (bytes < 1024) return bytes + ' B'
      if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
      return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
    },
    getFileIcon(name) {
      if (!name) return 'fa-solid fa-file'
      const ext = name.split('.').pop().toLowerCase()
      const iconMap = {
        exe: 'fa-solid fa-cube',
        msi: 'fa-solid fa-cube',
        dmg: 'fa-brands fa-apple',
        deb: 'fa-brands fa-linux',
        rpm: 'fa-brands fa-linux',
        AppImage: 'fa-brands fa-linux',
        apk: 'fa-brands fa-android',
        zip: 'fa-solid fa-file-zipper',
        'tar.gz': 'fa-solid fa-file-zipper',
        gz: 'fa-solid fa-file-zipper',
        '7z': 'fa-solid fa-file-zipper',
        rar: 'fa-solid fa-file-zipper'
      }
      return iconMap[ext] || 'fa-solid fa-file'
    },
    getSpeedBarWidth(proxy) {
      if (proxy.status !== 'done' || proxy.speed <= 0 || this.maxSpeed <= 0) {
        return proxy.status === 'testing' ? '60%' : '0%'
      }
      return Math.max(5, (proxy.speed / this.maxSpeed) * 100) + '%'
    },
    getArchTagLabel(fileName) {
      for (const rule of ARCH_RULES) {
        if (rule.pattern.test(fileName)) {
          let label = rule.label
          for (const cpu of CPU_RULES) {
            if (cpu.pattern.test(fileName)) {
              label += ' ' + cpu.label
              break
            }
          }
          return label
        }
      }
      return '其他'
    },
    getArchTagClass(fileName) {
      for (const rule of ARCH_RULES) {
        if (rule.pattern.test(fileName)) {
          return rule.tagClass
        }
      }
      return 'tag-generic'
    }
  },
  beforeUnmount() {
    if (this.autoDownloadTimer) {
      clearInterval(this.autoDownloadTimer)
      this.autoDownloadTimer = null
    }
  }
}
</script>

<style scoped>

.testing-header-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.mode-selector {
  display: flex;
  background: rgba(0, 0, 0, 0.05);
  padding: 4px;
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.03);
}

.mode-tab {
  border: none;
  background: none;
  padding: 6px 12px;
  border-radius: 8px;
  font-size: 13px;
  color: #666;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: '猫啃网糖圆体';
}

.mode-tab.active {
  background: white;
  color: #333;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.stage-mode-select {
  padding: 10px 0;
  animation: fadeIn 0.4s ease;
}

.mode-select-header {
  text-align: center;
  margin-bottom: 25px;
}

.file-badge {
  display: inline-flex;
  align-items: center;
  background: rgba(0, 0, 0, 0.05);
  padding: 6px 14px;
  border-radius: 20px;
  font-size: 13px;
  color: #666;
  margin-bottom: 12px;
}

.file-badge i {
  margin-right: 8px;
  color: #409eff;
}

.mode-title {
  font-family: '猫啃网糖圆体';
  font-size: 1.4em;
  color: #333;
  margin: 0;
}

.mode-cards {
  display: flex;
  flex-direction: column;
  gap: 15px;
  margin-bottom: 25px;
}

.mode-card {
  display: flex;
  align-items: center;
  padding: 18px 20px;
  border-radius: 18px;
  background: #fcfcfc;
  border: 1px solid #eee;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
}

.mode-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.05);
  border-color: #409eff;
  background: white;
}

.mode-card.auto:hover {
  border-color: #52c41a;
}

.card-icon {
  width: 50px;
  height: 50px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5em;
  margin-right: 18px;
  flex-shrink: 0;
}

.auto .card-icon {
  background: rgba(82, 196, 26, 0.1);
  color: #52c41a;
}

.manual .card-icon {
  background: rgba(64, 158, 255, 0.1);
  color: #409eff;
}

.card-content {
  flex-grow: 1;
}

.card-title {
  font-family: '猫啃网糖圆体';
  font-size: 1.1em;
  color: #333;
  margin-bottom: 4px;
}

.card-desc {
  font-size: 13px;
  color: #888;
  line-height: 1.4;
}

.card-arrow {
  color: #ccc;
  font-size: 1.2em;
  transition: all 0.3s;
}

.mode-card:hover .card-arrow {
  color: #333;
  transform: translateX(3px);
}

.back-btn {
  width: 100%;
  padding: 10px;
  background: none;
  border: 1px dashed #ddd;
  border-radius: 12px;
  color: #999;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s;
  font-family: '猫啃网糖圆体';
}

.back-btn:hover {
  color: #666;
  border-color: #bbb;
  background: rgba(0, 0, 0, 0.02);
}

.auto-ready-info {
  margin: 15px 0;
  padding: 12px 18px;
  background: linear-gradient(135deg, rgba(82, 196, 26, 0.1), rgba(139, 236, 59, 0.1));
  border: 1px solid rgba(82, 196, 26, 0.2);
  border-radius: 15px;
  display: flex;
  align-items: center;
  animation: slideInUp 0.4s ease-out;
}

.auto-pulse-icon {
  width: 40px;
  height: 40px;
  background: #52c41a;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2em;
  margin-right: 15px;
  flex-shrink: 0;
  box-shadow: 0 0 15px rgba(82, 196, 26, 0.3);
}

.auto-text {
  flex-grow: 1;
  font-size: 14px;
  color: #2b4c16;
  font-family: '猫啃网糖圆体';
}

.auto-text strong {
  font-size: 1.2em;
  color: #52c41a;
  margin: 0 4px;
}

.stop-auto-btn {
  border: 1px solid rgba(0, 0, 0, 0.1);
  background: white;
  padding: 4px 10px;
  border-radius: 8px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s;
  color: #666;
}

.stop-auto-btn:hover {
  background: #f5f5f5;
  border-color: rgba(0, 0, 0, 0.2);
}

.proxy-test-list.manual-mode .proxy-test-item.clickable {
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
}

.proxy-test-list.manual-mode .proxy-test-item.clickable:hover {
  transform: translateY(-2px);
  background: rgba(255, 255, 255, 0.9);
  border-color: #52c41a;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.08);
}

.proxy-test-list.manual-mode .proxy-test-item.clickable:active {
  transform: scale(0.98);
}

.manual-select-hint {
  position: absolute;
  right: 15px;
  top: 50%;
  transform: translateY(-50%);
  background: #52c41a;
  color: white;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 12px;
  opacity: 0;
  transition: all 0.3s;
  pointer-events: none;
}

.proxy-test-item.clickable:hover .manual-select-hint {
  opacity: 1;
  right: 10px;
}

@keyframes slideInUp {
  from {
    transform: translateY(10px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}



.modal-overlay {
  position: fixed;
  top: 0; left: 0;
  width: 100%; height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: var(--z-dialog);

}

.custom-modal {
  background: #fff;
  border-radius: 12px;
  width: 90%;
  max-width: 520px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  max-height: 85vh;
}

.modal-header {
  padding: 15px 20px;
  border-bottom: 1px solid #eee;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-header h3 {
  margin: 0;
  font-family: '猫啃网糖圆体';
  font-size: 1.2em;
  color: #333;
  display: flex;
  align-items: center;
}

.close-btn {
  background: none;
  border: none;
  font-size: 18px;
  cursor: pointer;
  color: #999;
  transition: color 0.3s;
}

.close-btn:hover { color: #333; }

.modal-body {
  padding: 20px;
  flex: 1;
  overflow-y: auto;
}

.modal-footer {
  padding: 15px 20px;
  border-top: 1px solid #eee;
  display: flex;
  justify-content: flex-end;
}

.cancel-btn {
  padding: 8px 20px;
  border: none;
  background: #f5f5f5;
  color: #666;
  border-radius: 20px;
  cursor: pointer;
  font-family: '爱点风雅黑';
  transition: all 0.3s;
}

.cancel-btn:hover { background: #e0e0e0; }

/* === Loading === */
.stage-loading {
  text-align: center;
  padding: 40px 0;
}

.loading-spinner-large {
  font-size: 2.5em;
  color: #409eff;
  margin-bottom: 15px;
}

.loading-text {
  color: #666;
  font-family: '猫啃网糖圆体';
}

/* === Error === */
.stage-error {
  text-align: center;
  padding: 30px 0;
}

.stage-empty {
  text-align: center;
  padding: 30px 0;
}

.empty-icon {
  font-size: 2.5em;
  color: #909399;
  margin-bottom: 15px;
}

.empty-title {
  color: #444;
  font-family: '猫啈网糖圆体';
  font-size: 1.1em;
  margin-bottom: 8px;
}

.empty-text {
  color: #999;
  font-family: '猫啈网糖圆体';
  font-size: 0.92em;
  margin-bottom: 20px;
  line-height: 1.6;
}

.empty-actions {
  display: flex;
  justify-content: center;
  gap: 12px;
  align-items: center;
}

.empty-link-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 20px;
  border: 1px solid #dcdfe6;
  background: transparent;
  color: #606266;
  border-radius: 20px;
  cursor: pointer;
  font-family: '爱点风雅黑';
  font-size: 0.9em;
  text-decoration: none;
  transition: all 0.3s;
}

.empty-link-btn:hover {
  border-color: #409eff;
  color: #409eff;
}

.error-icon {
  font-size: 2.5em;
  color: #f56c6c;
  margin-bottom: 15px;
}

.error-text {
  color: #666;
  font-family: '猫啃网糖圆体';
  margin-bottom: 20px;
}

.retry-btn {
  padding: 8px 24px;
  border: none;
  background: linear-gradient(135deg, #409eff, #337ecc);
  color: white;
  border-radius: 20px;
  cursor: pointer;
  font-family: '爱点风雅黑';
  transition: all 0.3s;
}

.retry-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(64, 158, 255, 0.3);
}

/* === File Select === */
.release-info {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
}

.release-tag {
  background: linear-gradient(135deg, #24292e, #40464b);
  color: #fff;
  padding: 4px 12px;
  border-radius: 15px;
  font-family: 'Consolas', monospace;
  font-size: 0.85em;
}

.release-date {
  color: #999;
  font-size: 0.85em;
  font-family: '猫啃网糖圆体';
}

.tip-text {
  margin-bottom: 15px;
  color: #e6a23c;
  background: #fdf6ec;
  padding: 10px;
  border-radius: 8px;
  font-size: 0.85em;
  display: flex;
  align-items: center;
  gap: 8px;
}

.file-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 350px;
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 4px;
}

.file-item {
  width: 100%;
  display: flex;
  align-items: center;
  padding: 12px 15px;
  border: 1px solid #eee;
  border-radius: 10px;
  background: #fff;
  cursor: pointer;
  transition: all 0.3s;
  text-align: left;
}

.file-item:hover {
  border-color: #409eff;
  background: #f0f7ff;
}

.file-icon {
  width: 36px;
  height: 36px;
  background: #f0f7ff;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
  color: #409eff;
  font-size: 1.1em;
  flex-shrink: 0;
}

.file-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.file-name {
  font-weight: bold;
  color: #333;
  font-family: '爱点风雅黑';
  font-size: 0.9em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.file-meta {
  font-size: 0.75em;
  color: #999;
  margin-top: 2px;
}

.file-dl-icon {
  color: #ccc;
  font-size: 0.9em;
  flex-shrink: 0;
  margin-left: 8px;
}

.file-right {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  margin-left: 8px;
}

.recommend-section {
  margin-bottom: 10px;
}

.recommend-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: linear-gradient(135deg, #4CAF50, #2E7D32);
  color: #fff;
  padding: 3px 12px;
  border-radius: 12px;
  font-size: 0.8em;
  font-weight: bold;
  margin-bottom: 10px;
  font-family: '爱点风雅黑';
}

.recommend-badge i {
  font-size: 0.9em;
}

.recommend-item {
  border-color: #4CAF50 !important;
  background: rgba(76, 175, 80, 0.06) !important;
  border-width: 2px !important;
}

.recommend-item:hover {
  background: rgba(76, 175, 80, 0.1) !important;
  border-color: #2E7D32 !important;
  box-shadow: 0 10px 25px rgba(76, 175, 80, 0.15);
}

.recommend-icon {
  background: rgba(76, 175, 80, 0.1) !important;
  color: #4CAF50 !important;
}

.arch-tag {
  padding: 3px 10px;
  border-radius: 10px;
  font-size: 0.75em;
  font-weight: bold;
  white-space: nowrap;
  font-family: '爱点风雅黑';
  letter-spacing: 0.3px;
}

.tag-windows {
  background: rgba(76, 175, 80, 0.1);
  color: #2e7d32;
  border: 1px solid rgba(76, 175, 80, 0.2);
}

.tag-macos {
  background: rgba(64, 158, 255, 0.1);
  color: #1565c0;
  border: 1px solid rgba(64, 158, 255, 0.2);
}

.tag-linux {
  background: rgba(255, 152, 0, 0.1);
  color: #e65100;
  border: 1px solid rgba(255, 152, 0, 0.2);
}

.tag-android {
  background: rgba(76, 175, 80, 0.1);
  color: #388e3c;
  border: 1px solid rgba(76, 175, 80, 0.2);
}

.tag-generic {
  background: rgba(144, 147, 153, 0.1);
  color: #666;
  border: 1px solid rgba(144, 147, 153, 0.15);
}

/* === Speed Test === */
.testing-header {
  font-family: '猫啃网糖圆体';
  font-size: 1em;
  color: #409eff;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.testing-file {
  background: #f5f7fa;
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 0.85em;
  color: #606266;
  margin-bottom: 15px;
  display: flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.proxy-test-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.proxy-test-item {
  padding: 10px 14px;
  border-radius: 8px;
  background: #f9f9f9;
  border: 1px solid #eee;
  transition: all 0.3s;
}

.proxy-test-item.is-testing {
  border-color: #409eff;
  background: #ecf5ff;
}

.proxy-test-item.is-fastest {
  border-color: #67c23a;
  background: #f0f9eb;
}

.proxy-test-item.is-failed {
  opacity: 0.6;
}

.proxy-name {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
  font-size: 0.85em;
  font-family: '爱点风雅黑';
  color: #333;
}

.proxy-spinner { color: #409eff; }
.proxy-ok { color: #67c23a; }
.proxy-fail { color: #f56c6c; }
.proxy-wait { color: #ddd; font-size: 0.6em; }

.fastest-badge {
  background: linear-gradient(135deg, #67c23a, #529b2e);
  color: #fff;
  padding: 1px 8px;
  border-radius: 10px;
  font-size: 0.75em;
  font-weight: bold;
}

.server-badge {
  margin-left: 6px;
  font-size: 10px;
  color: #409eff;
  border: 1px solid #a0cfff;
  border-radius: 3px;
  padding: 0 4px;
  line-height: 16px;
  flex-shrink: 0;
}

.proxy-speed-bar {
  display: flex;
  align-items: center;
  gap: 10px;
}

.speed-bar-bg {
  flex: 1;
  height: 6px;
  background: #e4e7ed;
  border-radius: 3px;
  overflow: hidden;
}

.speed-bar-fill {
  height: 100%;
  background: linear-gradient(90deg, #409eff, #67c23a);
  border-radius: 3px;
  transition: width 0.5s ease;
}

.is-testing .speed-bar-fill {
  background: linear-gradient(90deg, #409eff, #79bbff);
  animation: pulse-bar 1s ease-in-out infinite;
}

@keyframes pulse-bar {
  0%, 100% { opacity: 0.6; }
  50% { opacity: 1; }
}

.is-fastest .speed-bar-fill {
  background: linear-gradient(90deg, #67c23a, #95d475);
}

.speed-value {
  font-size: 0.75em;
  color: #67c23a;
  font-family: 'Consolas', monospace;
  white-space: nowrap;
  min-width: 70px;
  text-align: right;
}

.speed-fail { color: #f56c6c; }
.speed-testing { color: #409eff; }

.no-proxy-tip {
  margin-top: 15px;
  background: #fef0f0;
  color: #f56c6c;
  padding: 10px;
  border-radius: 6px;
  font-size: 0.85em;
  display: flex;
  align-items: center;
  gap: 8px;
}

/* === Download === */
.stage-downloading {
  text-align: center;
  padding: 30px 0;
}

.download-success-icon {
  font-size: 3em;
  color: #67c23a;
  margin-bottom: 12px;
}

.download-text {
  font-family: '猫啃网糖圆体';
  font-size: 1.1em;
  color: #333;
  margin-bottom: 8px;
}

.download-file-name {
  color: #999;
  font-size: 0.85em;
  margin-bottom: 12px;
  word-break: break-all;
}

.download-proxy {
  color: #67c23a;
  font-size: 0.85em;
  margin-bottom: 15px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

.manual-download-link {
  color: #409eff;
  font-size: 0.8em;
  text-decoration: none;
  transition: color 0.2s;
}

.manual-download-link:hover {
  color: #337ecc;
  text-decoration: underline;
}

/* === Dark Theme === */
.dark-theme .custom-modal { background: #2d2d2d; color: #fff; }
.dark-theme .modal-header { border-bottom-color: #3d3d3d; }
.dark-theme .modal-header h3 { color: #fff; }
.dark-theme .close-btn { color: #aaa; }
.dark-theme .close-btn:hover { color: #fff; }
.dark-theme .modal-footer { border-top-color: #3d3d3d; }
.dark-theme .cancel-btn { background: #383838; color: #ccc; }
.dark-theme .cancel-btn:hover { background: #444; }
.dark-theme .loading-text { color: #aaa; }
.dark-theme .error-text { color: #aaa; }
.dark-theme .empty-title { color: #ccc; }
.dark-theme .empty-text { color: #888; }
.dark-theme .empty-link-btn { color: #aaa; border-color: #555; }
.dark-theme .empty-link-btn:hover { color: #66b1ff; border-color: #66b1ff; }
.dark-theme .tip-text { background: #3a3225; }
.dark-theme .release-date { color: #666; }
.dark-theme .file-item { background: #383838; border-color: #444; }
.dark-theme .file-item:hover { border-color: #409eff; background: #2a3a4a; }
.dark-theme .file-icon { background: #2a3a4a; }
.dark-theme .file-name { color: #eee; }
.dark-theme .file-meta { color: #666; }
.dark-theme .testing-file { background: #383838; color: #aaa; }
.dark-theme .proxy-test-item { background: #383838; border-color: #444; }
.dark-theme .proxy-test-item.is-testing { border-color: #409eff; background: #2a3a4a; }
.dark-theme .proxy-test-item.is-fastest { border-color: #67c23a; background: #2a3a2a; }
.dark-theme .proxy-name { color: #ccc; }
.dark-theme .proxy-wait { color: #555; }
.dark-theme .speed-bar-bg { background: #444; }
.dark-theme .no-proxy-tip { background: #3a2525; }
.dark-theme .download-text { color: #eee; }
.dark-theme .download-file-name { color: #666; }

/* 补充新增元素的深色模式样式 */
.dark-theme .mode-title { color: #fff; }
.dark-theme .mode-card { background: #383838; border-color: #444; }
.dark-theme .mode-card:hover { background: #444; border-color: #409eff; }
.dark-theme .card-title { color: #eee; }
.dark-theme .card-desc { color: #888; }
.dark-theme .mode-selector { background: rgba(255, 255, 255, 0.05); border-color: #444; }
.dark-theme .mode-tab { color: #aaa; }
.dark-theme .mode-tab.active { background: #444; color: #fff; }
.dark-theme .file-badge { background: rgba(255, 255, 255, 0.08); color: #aaa; }
.dark-theme .stop-auto-btn { background: #383838; border-color: #555; color: #aaa; }
.dark-theme .stop-auto-btn:hover { background: #444; color: #fff; }
.dark-theme .auto-text { color: #a5d6a7; }
.dark-theme .auto-ready-info { background: linear-gradient(135deg, rgba(82, 196, 26, 0.08), rgba(139, 236, 59, 0.08)); border-color: rgba(82, 196, 26, 0.2); }
.dark-theme .proxy-test-item.clickable:hover { background: rgba(255, 255, 255, 0.05); }
.dark-theme .mode-selector { background: rgba(0, 0, 0, 0.2); border: 1px solid rgba(255, 255, 255, 0.1); }

.dark-theme .recommend-item { background: rgba(76, 175, 80, 0.08) !important; border-color: #4CAF50 !important; }
.dark-theme .recommend-item:hover { background: rgba(76, 175, 80, 0.15) !important; border-color: #4CAF50 !important; }
.dark-theme .recommend-icon { background: rgba(76, 175, 80, 0.15) !important; color: #4CAF50 !important; }
.dark-theme .recommend-badge { background: linear-gradient(135deg, #4CAF50, #2E7D32); }
.dark-theme .tag-windows { background: rgba(76, 175, 80, 0.15); color: #81c784; border-color: rgba(76, 175, 80, 0.3); }
.dark-theme .tag-macos { background: rgba(64, 158, 255, 0.15); color: #79bbff; border-color: rgba(64, 158, 255, 0.3); }
.dark-theme .tag-linux { background: rgba(255, 152, 0, 0.15); color: #eebe77; border-color: rgba(255, 152, 0, 0.3); }
.dark-theme .tag-android { background: rgba(76, 175, 80, 0.15); color: #81c784; border-color: rgba(76, 175, 80, 0.3); }
.dark-theme .tag-generic { background: rgba(144, 147, 153, 0.15); color: #b1b3b8; border-color: rgba(144, 147, 153, 0.3); }

/* === Animations === */
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.3s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

.modal-fade-enter-active .custom-modal {
  transition: transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.modal-fade-leave-active .custom-modal {
  transition: transform 0.3s ease-in;
}

.modal-fade-enter-from .custom-modal {
  transform: scale(0.9) translateY(10px);
}

.modal-fade-leave-to .custom-modal {
  transform: scale(0.95) translateY(10px);
}

/* === Mobile === */
@media (max-width: 480px) {
  .custom-modal {
    width: 100%;
    border-radius: 20px 20px 0 0;
    position: absolute;
    bottom: 0;
    margin: 0;
    max-width: none;
    max-height: 90vh;
  }

  .modal-overlay {
    align-items: flex-end;
  }

  .modal-fade-enter-from .custom-modal,
  .modal-fade-leave-to .custom-modal {
    transform: translateY(100%);
  }
}

/* === Linux Distro Picker === */
.distro-hint {
  font-size: 0.9em;
  color: #666;
  margin: 0 0 20px 0;
  font-family: '爱点风雅黑';
  line-height: 1.6;
}

.distro-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  margin-bottom: 20px;
}

.distro-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 18px 12px;
  border: 2px solid rgba(0, 0, 0, 0.06);
  border-radius: 16px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
  background: rgba(255, 255, 255, 0.4);
  text-align: center;
}

.distro-card:hover {
  border-color: #4CAF50;
  background: rgba(76, 175, 80, 0.06);
  transform: translateY(-3px);
  box-shadow: 0 8px 20px rgba(76, 175, 80, 0.12);
}

.distro-card-selected {
  border-color: #4CAF50 !important;
  background: rgba(76, 175, 80, 0.1) !important;
  box-shadow: 0 4px 15px rgba(76, 175, 80, 0.2);
}

.distro-icon {
  font-size: 2em;
  color: #4CAF50;
  margin-bottom: 8px;
}

.distro-name {
  font-weight: bold;
  font-size: 0.9em;
  color: #333;
  font-family: '爱点风雅黑';
  margin-bottom: 4px;
}

.distro-desc {
  font-size: 0.75em;
  color: #999;
  font-family: '爱点风雅黑';
}

.distro-skip {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px;
  color: #999;
  font-size: 0.85em;
  cursor: pointer;
  border-radius: 12px;
  transition: all 0.3s;
  font-family: '爱点风雅黑';
}

.distro-skip:hover {
  color: #666;
  background: rgba(0, 0, 0, 0.03);
}

.distro-reset-btn {
  padding: 8px 18px;
  border: 1px solid rgba(255, 152, 0, 0.3);
  background: rgba(255, 152, 0, 0.08);
  color: #e65100;
  border-radius: 18px;
  cursor: pointer;
  font-family: '爱点风雅黑';
  font-size: 0.85em;
  transition: all 0.3s;
  margin-right: auto;
}

.distro-reset-btn:hover {
  background: rgba(255, 152, 0, 0.15);
  border-color: rgba(255, 152, 0, 0.5);
  transform: translateY(-2px);
}

.dark-theme .distro-card {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.08);
}

.dark-theme .distro-card:hover {
  border-color: #4CAF50;
  background: rgba(76, 175, 80, 0.08);
}

.dark-theme .distro-card-selected {
  border-color: #4CAF50 !important;
  background: rgba(76, 175, 80, 0.12) !important;
}

.dark-theme .distro-icon {
  color: #81c784;
}

.dark-theme .distro-name {
  color: #f5f5f7;
}

.dark-theme .distro-desc {
  color: rgba(255, 255, 255, 0.4);
}

.dark-theme .distro-hint {
  color: rgba(255, 255, 255, 0.6);
}

.dark-theme .distro-skip {
  color: rgba(255, 255, 255, 0.4);
}

.dark-theme .distro-skip:hover {
  color: rgba(255, 255, 255, 0.7);
  background: rgba(255, 255, 255, 0.05);
}

.dark-theme .distro-reset-btn {
  background: rgba(255, 152, 0, 0.1);
  border-color: rgba(255, 152, 0, 0.3);
  color: #eebe77;
}

.dark-theme .distro-reset-btn:hover {
  background: rgba(255, 152, 0, 0.2);
  border-color: rgba(255, 152, 0, 0.5);
}

@media (max-width: 480px) {
  .distro-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 8px;
  }

  .distro-card {
    padding: 14px 8px;
  }

  .distro-icon {
    font-size: 1.5em;
  }
}

.system-incompatible-warning {
  display: flex;
  align-items: center;
  gap: 12px;
  background: rgba(255, 71, 87, 0.1);
  border: 1px solid rgba(255, 71, 87, 0.3);
  color: #ff4757;
  padding: 15px 20px;
  border-radius: 16px;
  margin-bottom: 20px;
  font-family: '爱点风雅黑';
  font-size: 0.9em;
  line-height: 1.4;
  box-shadow: 0 4px 15px rgba(255, 71, 87, 0.05);
}

.system-incompatible-warning i {
  font-size: 1.2em;
  flex-shrink: 0;
}

.dark-theme .system-incompatible-warning {
  background: rgba(255, 71, 87, 0.15);
  border-color: rgba(255, 71, 87, 0.4);
  color: #ff6b81;
}
</style>
